import React, { useMemo, useState } from 'react';
import { ButtonBase, Chip, Popover, Stack, Tooltip, Typography } from '@mui/material';
import BookingCycle, { bookingLifecycleLabels, getRecordedLifecycleEvents } from './BookingCycle';
import { BookingLifecycle, IExternalBooking } from '../types/bookings';

type Track = {
    key: 'booking' | 'centreVisit' | 'homeCollection';
    label: string;
    lifecycle?: BookingLifecycle;
    status?: string;
    hasReport?: boolean;
    muted?: boolean;
};

// Each colour keeps at least 4.5:1 contrast against its own tinted chip background.
const eventColors: Partial<Record<keyof BookingLifecycle, string>> = {
    created: '#475569', paymentPending: '#92400e', paid: '#075985', confirmed: '#1d4ed8',
    rescheduled: '#6d28d9', collectorAssigned: '#155e75', sampleCollected: '#115e59',
    patientVisited: '#134e4a', reportAdded: '#166534', reportDelivered: '#14532d', completed: '#14532d',
    cancelled: '#b91c1c', abandoned: '#9a3412', refunded: '#334155',
};

const terminalStatuses = new Set(['cancelled', 'canceled', 'abandoned', 'refunded', 'rejected', 'failed']);
const normalize = (value?: string) => (value || '').trim().toLowerCase();
const legStatus = (leg?: { status?: string; normalizedStatus?: string }) =>
    leg?.normalizedStatus && leg.normalizedStatus !== 'unknown' ? leg.normalizedStatus : leg?.status;
const statusLabel = (value?: string) => value
    ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, character => character.toUpperCase())
    : 'No status received';

function LifecycleTrack({ track, skipReport, expanded, onOpen }: {
    track: Track;
    skipReport: boolean;
    expanded: boolean;
    onOpen: (event: React.MouseEvent<HTMLElement>, track: Track) => void;
}) {
    const events = getRecordedLifecycleEvents(track.lifecycle);
    const terminal = terminalStatuses.has(normalize(track.status));

    return (
        <Tooltip title="View lifecycle timestamps" placement="top" arrow>
            <ButtonBase
                onClick={(event) => onOpen(event, track)}
                aria-label={`View ${track.label} lifecycle. Current status: ${statusLabel(track.status)}`}
                aria-haspopup="dialog"
                aria-expanded={expanded}
                sx={{
                    display: 'block', width: '100%', borderRadius: 1.5, px: 0.75, py: 0.6,
                    textAlign: 'left', border: '1px dashed', borderColor: track.muted ? 'divider' : 'transparent',
                    transition: 'background-color 160ms ease-out',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" fontWeight={700}>{track.label}</Typography>
                    <Typography variant="caption" color={terminal ? 'error.main' : 'text.secondary'} fontWeight={terminal ? 700 : 500}>
                        {statusLabel(track.status)}
                    </Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                    {events.map(event => (
                        <Chip
                            key={event.key}
                            size="small"
                            label={bookingLifecycleLabels[event.key]}
                            sx={{
                                height: 22, fontSize: 11, fontWeight: 600, bgcolor: `${eventColors[event.key] || '#64748b'}14`,
                                color: eventColors[event.key] || 'text.primary', border: '1px solid',
                                borderColor: `${eventColors[event.key] || '#64748b'}45`,
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    ))}
                    {!events.length && (
                        <Typography variant="caption" color="text.secondary">No lifecycle events recorded</Typography>
                    )}
                    {track.hasReport && !track.lifecycle?.reportAdded && !track.lifecycle?.reportDelivered && (
                        <Chip size="small" label="Report stored" color="success" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                    )}
                    {skipReport && (
                        <Chip size="small" label="Report not required" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                    )}
                </Stack>
            </ButtonBase>
        </Tooltip>
    );
}

export default function BookingLifecycleOverview({ booking }: { booking: IExternalBooking }) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
    const isHybrid = booking.packageType === 'HYBRID';

    const tracks = useMemo<Track[]>(() => {
        const reportDocuments = booking.documents?.filter(document => document.type === 'report') || [];
        if (!isHybrid) {
            return [{
                key: 'booking', label: 'Booking', lifecycle: booking.lifecycle, status: booking.status,
                hasReport: Boolean(booking.report?.length || reportDocuments.length),
            }];
        }

        const parentTerminal = terminalStatuses.has(normalize(booking.overallStatus || booking.status));
        const hasUnattributedReport = reportDocuments.some(document =>
            !document.leg && !document.externalAppointmentId
        ) || Boolean(booking.report?.length && !reportDocuments.length);
        const hasLegReport = (leg: 'CENTRE_VISIT' | 'HOME_COLLECTION', appointmentIndex: number) =>
            reportDocuments.some(document =>
                document.leg === leg ||
                Boolean(document.externalAppointmentId && document.externalAppointmentId === booking.externalBookingIds?.[appointmentIndex])
            );
        return [
            {
                key: 'booking', label: 'Booking', lifecycle: booking.lifecycle,
                status: booking.overallStatus || booking.status,
                hasReport: hasUnattributedReport,
            },
            {
                key: 'centreVisit', label: 'Centre visit', lifecycle: booking.visitLifecycle?.centreVisit,
                status: legStatus(booking.legs?.centreVisit),
                hasReport: hasLegReport('CENTRE_VISIT', 0), muted: parentTerminal,
            },
            {
                key: 'homeCollection', label: 'Home collection', lifecycle: booking.visitLifecycle?.homeCollection,
                status: legStatus(booking.legs?.homeCollection),
                hasReport: hasLegReport('HOME_COLLECTION', 1), muted: parentTerminal,
            },
        ];
    }, [booking, isHybrid]);

    const openTrack = (event: React.MouseEvent<HTMLElement>, track: Track) => {
        setAnchorEl(event.currentTarget);
        setSelectedTrack(track);
    };
    const closePopover = () => { setAnchorEl(null); setSelectedTrack(null); };

    return (
        <>
            <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                {tracks.map(track => (
                    <LifecycleTrack
                        key={track.key}
                        track={track}
                        skipReport={Boolean(booking.skipReport && track.key === 'booking')}
                        expanded={Boolean(anchorEl) && selectedTrack?.key === track.key}
                        onOpen={openTrack}
                    />
                ))}
            </Stack>
            <Popover
                open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={closePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    role: 'dialog',
                    'aria-label': selectedTrack ? `${selectedTrack.label} lifecycle details` : 'Booking lifecycle details',
                    sx: { width: 360, maxWidth: 'calc(100vw - 32px)', p: 2.25, mt: 0.75 },
                }}
            >
                <BookingCycle
                    lifecycle={selectedTrack?.lifecycle}
                    createdAt={selectedTrack?.key === 'booking' ? booking.createdAt : undefined}
                    title={selectedTrack ? `${selectedTrack.label} lifecycle` : 'Booking lifecycle'}
                    external
                />
            </Popover>
        </>
    );
}
